import {
  GlobalStyles,
  StyledEngineProvider,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material';
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';

import { getTheme } from 'constants/theme';
import { useWindowDimensions } from 'hooks/common';
import { useSlimDesign } from 'utils/useSlimDesign';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { changeThemeMode, ThemeModeSelector } from 'store/common';

interface IProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const themeMode = useAppSelector(ThemeModeSelector);

  const theme = useMemo(() => getTheme(themeMode), [themeMode]);
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();
  const isSlim = useSlimDesign();

  const getThemeOnLoad = useCallback(() => {
    const themeFromLocalStorage = localStorage.getItem('theme');
    const userPreferredMode = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';

    // On First Load Check User Preferred Mode
    if (!themeFromLocalStorage) {
      dispatch(changeThemeMode(userPreferredMode));
      return;
    }

    // On Second Load get Data from localStorage
    if (themeFromLocalStorage === 'light' || themeFromLocalStorage === 'dark') {
      dispatch(changeThemeMode(themeFromLocalStorage));
    }
  }, []);

  useLayoutEffect(() => {
    getThemeOnLoad();

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      dispatch(
        changeThemeMode(
          window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
        ),
      );
    });
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <LegacyThemeProvider theme={theme}>
        <MuiThemeProvider theme={theme}>
          <GlobalStyles
            styles={{
              '*': {
                fontFamily: "'Nunito', sans-serif",
                fontSize: width < 450 ? 'small' : 'unset',
              },
              '.MuiOutlinedInput-notchedOutline > legend > span': { fontSize: 'inherit' },
              '.ql-clipboard': { display: 'none !important' },
              ...(isSlim && {
                '.MuiDialog-paper': {
                  margin: '0 !important',
                  maxWidth: '100% !important',
                  maxHeight: '100% !important',
                  width: '100% !important',
                  height: '100% !important',
                  borderRadius: '0 !important',
                },
              }),
            }}
          />
          {children}
        </MuiThemeProvider>
      </LegacyThemeProvider>
    </StyledEngineProvider>
  );
};

export default ThemeProvider;
